import angular from 'angular'
import {
  ApiAuthorizationServiceData,
  ApiAuthorizationServiceType
} from '../api-keys/api-authorization.service'

export function DashboardSwitchCtrl($scope, showNewDashboard) {
  'ngInject'
  $scope.doShowNewDashboard = () => showNewDashboard
}

interface NewDashboardCtrlScope {
  model: ApiAuthorizationServiceData
  onBackToOldDashboard: () => void
}

export function NewDashboardCtrl(
  $scope: NewDashboardCtrlScope,
  dashboardToggleService,
  apiAuthorizationService: ApiAuthorizationServiceType,
  permissions
) {
  'ngInject'

  angular.extend($scope, {
    model: apiAuthorizationService.getInitialData(),
    onBackToOldDashboard: () => dashboardToggleService.onBackToOldDashboard()
  })

  if (!permissions.isImpersonating()) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    apiAuthorizationService
      .init()
      .then(({ apiKey, authHeader, teamserverURL }) => {
        $scope.model.apiKey = apiKey
        $scope.model.authHeader = authHeader
        $scope.model.teamserverURL = teamserverURL
      })
  }
}

export function DashboardCtrl(
  $scope,
  $routeSegment,
  $location,
  $q,
  permissions,
  tutorialModalService,
  ErrorService,
  UserService,
  TrendService,
  ChartService,
  LoadFilterRouteService,
  FeaturesService,
  ScoreFactory,
  OrganizationFactory,
  HistoryFactory,
  UserFactory,
  LicenseFactory,
  SCORE_TYPE,
  URLHistoryService,
  dashboardToggleService
) {
  'ngInject'

  angular.extend($scope, {
    model: {
      scores: {},
      defense: {},
      assess: false,
      defend: false,
      ads: {},
      trends: {
        score: {},
        attack: {}
      },
      stats: {
        application: {},
        library: {},
        server: {}
      },
      environments: {
        dev: 0,
        qa: 0,
        prod: 0
      },
      disabledData: {}
    },
    breakdown: { unused: 0 },
    scoresPromise: false,
    scoreBarsPromise: false,
    scoreTrendPromise: false,
    attackTrendPromise: false,
    appStatsPromise: false,
    libStatsPromise: false,
    serverStatsPromise: false,
    scoreType: SCORE_TYPE,
    orgUuid: $routeSegment.$routeParams.orgUuid,
    linkToVulnerableLibs: ''
  })

  angular.extend($scope, {
    showAssess: () => {
      $location.path(UserService.buildUrl('/servers'))
      tutorialModalService.openAssess()
    },
    showDefenseSetup: () => {
      $location.path(UserService.buildUrl('/servers'))
      tutorialModalService.openProtectAll()
    },
    showHighRiskApplications: () => {
      LoadFilterRouteService.goToApps('HIGH_RISK')
    },
    showOfflineServers: () => {
      LoadFilterRouteService.goToFilteredServers('OFFLINE')
    },
    closeAd: () => {
      if ($scope.showLicenseAd()) {
        $scope.model.ads.licenses = false
      } else {
        $scope.model.ads.setup = false
      }
      updateUserAdSettings($scope.model.ads.licenses, $scope.model.ads.setup)
    },

    showLicenseAd: () => {
      return (
        $scope.model.ads.licenses &&
        !($scope.breakdown.unused > 0 && $scope.model.raspEnabled)
      )
    },
    showSetupAd: () => {
      return (
        $scope.model.ads.setup &&
        $scope.breakdown.unused > 0 &&
        $scope.model.raspEnabled
      )
    },
    hasPermission: (permission) => {
      return permissions.hasPermission(permission)
    },
    enableNewDashboard: () => dashboardToggleService.enableNewDashboard()
  })

  function init() {
    const previousUrl = URLHistoryService.getPreviousUrl()
    if (previousUrl) {
      $location.url(previousUrl, true)
      URLHistoryService.clearPreviousUrl()
    }
    $q.all([
      FeaturesService.loadFeatureFlags(),
      permissions.getUserAccessLoaded()
    ]).then(() => {
      $scope.model.raspEnabled = isRASPEnabled()

      getScoreTrend().then(() => {
        $scope.scoreTrendPromise = true
      })

      const scoresPromises = [
        getScores().then(() => {
          $scope.scoresPromise = true
        })
      ]

      if (isRASPEnabled()) {
        scoresPromises.push(getDefenseScores())

        getAttackTrend().then(() => {
          $scope.attackTrendPromise = true
        })
      }

      $q.all(scoresPromises).then(() => {
        $scope.scoreBarsPromise = true
      })

      getApplicationStats().then(() => {
        $scope.appStatsPromise = true
      })
      getLibraryStats().then(() => {
        $scope.libStatsPromise = true
      })
      getServerStats().then(() => {
        $scope.serverStatsPromise = true
      })

      getUserAdSettings()
      getLicenses()

      getDisabledSparklineData()

      setLinkToVulnerableLibs()
    })
  }

  function getScores() {
    return ScoreFactory.getScores().then(
      (response) => {
        $scope.model.scores = response.data.scores
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function getDefenseScores() {
    return ScoreFactory.getDefenseScores().then(
      (response) => {
        $scope.model.defense = response.data.scores
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function getScoreTrend() {
    return HistoryFactory.getScoreHistoryByInterval('WEEK').then(
      (response) => {
        $scope.model.trends.score = TrendService.getScoreTrendInfo(
          response.data.total
        )
        if (response.data.total.length > 0) {
          $scope.model.trends.score.data =
            ChartService.buildSparklineScoreTrendData(response.data.total)
        }
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function getAttackTrend() {
    return HistoryFactory.getAttacksHistoryByInterval('WEEK').then(
      (response) => {
        $scope.model.trends.attack = TrendService.getAttackTrendInfo(
          response.data.items
        )
        if (response.data.items.length > 0) {
          $scope.model.trends.attack.data =
            ChartService.buildSparklineAttackTrendData(response.data.items)
        }
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function getApplicationStats() {
    return OrganizationFactory.getApplicationStats('WEEK', 'previous').then(
      (response) => {
        $scope.model.stats.application = response.data
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function getLibraryStats() {
    return OrganizationFactory.getLibraryStats('WEEK', 'previous').then(
      (response) => {
        $scope.model.stats.library = response.data
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function getServerStats() {
    return OrganizationFactory.getServerStats('WEEK', 'previous').then(
      (response) => {
        $scope.model.stats.server = response.data
        $scope.model.environments.dev = $scope.model.stats.server.dev
        $scope.model.environments.qa = $scope.model.stats.server.qa
        $scope.model.environments.prod = $scope.model.stats.server.prod
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function getUserAdSettings() {
    return UserFactory.getAdPreferences().then(
      (response) => {
        angular.extend($scope.model.ads, {
          licenses: response.data.licenses,
          setup: response.data.setup
        })
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function updateUserAdSettings(licenses, setup) {
    return UserFactory.updateAdPreferences(licenses, setup).catch(
      ErrorService.processError
    )
  }

  function getLicenses() {
    return LicenseFactory.getAssessmentBreakdown().then(
      (response) => {
        $scope.breakdown = response.data.breakdown
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function getDisabledSparklineData() {
    $scope.model.disabledData = {
      up: ChartService.buildDisabledSparklineData(true),
      down: ChartService.buildDisabledSparklineData(false)
    }
  }

  function isRASPEnabled() {
    return permissions.isRASPEnabled()
  }

  function setLinkToVulnerableLibs() {
    $scope.linkToVulnerableLibs =
      LoadFilterRouteService.buildURLToOrganizationLibraries(true)
  }

  init()
}
